import {Box, Button, Divider, Menu, TextField, Typography} from "@mui/material";
import {RichTreeView} from "@mui/x-tree-view";
import {useEffect, useMemo, useState} from "react";
import {
    buildLabeledFilterTree,
    buildTreeIdToLabelMap,
    filterAccountTreeBySearch
} from "../../utils/filters";

export const AccountPicker = ({accounts, businessUnits, accountSetter, filters}) => {
    const [anchorElement, setAnchorElement] = useState(null);
    const [selectedIds, setSelectedIds] = useState(['ALL']);
    const [selectedName, setSelectedName] = useState('ALL');
    const [searchQuery, setSearchQuery] = useState('');

    const labeledAccounts = useMemo(
        () => buildLabeledFilterTree(
            accounts,
            businessUnits || [],
            filters.businessUnitId
        ),
        [accounts, businessUnits, filters.businessUnitId]
    );

    const filteredAccounts = useMemo(
        () => filterAccountTreeBySearch(labeledAccounts, searchQuery),
        [labeledAccounts, searchQuery]
    );

    const treeItems = useMemo(
        () => [{id: 'ALL', label: 'ALL'}, ...filteredAccounts],
        [filteredAccounts]
    );

    const accountIdToNameMap = useMemo(
        () => buildTreeIdToLabelMap(
            [{id: 'ALL', label: 'ALL'}, ...labeledAccounts]
        ),
        [labeledAccounts]
    );

    useEffect(() => {
        if (!filters.accountId) {
            setSelectedIds(['ALL']);
            setSelectedName('ALL');
            return;
        }
        setSelectedIds([filters.accountId]);
        setSelectedName(accountIdToNameMap[filters.accountId] ?? 'ALL');
    }, [filters.accountId, accountIdToNameMap]);

    const selectAccount = (selectedId) => {
        if (selectedId === 'ALL') {
            setSelectedIds(['ALL']);
            setSelectedName('ALL');
            accountSetter('ALL');
            return;
        }
        setSelectedIds([selectedId]);
        setSelectedName(accountIdToNameMap[selectedId] ?? 'ALL');
        accountSetter(selectedId);
    };

    const handleClick = (event) => {
        setAnchorElement(event.currentTarget);
    };

    const handleTreeSelection = (event, id) => {
        const selectedId = Array.isArray(id) ? id[0] : id;
        if (selectedId === selectedIds[0]) {
            return;
        }
        setAnchorElement(null);
        setSearchQuery('');
        selectAccount(selectedId);
    };

    const handleClose = () => {
        setAnchorElement(null);
        setSearchQuery('');
    };

    return (
        <div>
            <Typography variant="h7" display="inline">Account:</Typography>
            <Button onClick={handleClick} style={{textAlign: 'left'}}>{selectedName}</Button>
            <Menu
                id="accountPickerMenu"
                anchorEl={anchorElement}
                keepMounted
                open={Boolean(anchorElement)}
                onClose={handleClose}
            >
                <Box
                    role="presentation"
                    sx={{width: 600, mx: 2.5, my: 1}}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    <TextField
                        id="accountSearchField"
                        label="Search"
                        margin="normal"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                    />
                    <Divider sx={{my: 1}}/>
                    <Box sx={{maxHeight: 360, overflow: 'auto', pb: 1}}>
                        <RichTreeView
                            items={treeItems}
                            selectedItems={selectedIds}
                            onSelectedItemsChange={handleTreeSelection}
                            checkboxSelection
                        />
                    </Box>
                </Box>
            </Menu>
        </div>
    );
};
